/**
 * One live proposal per card, shared by every view that draws the card: the
 * chat card, the drawer's row, a conflict option and a collision hover.
 *
 * WHY one instance
 *   Each view used to draw a card its own way, and the drawer drew it from the
 *   texts stored when the card was proposed. Once another card or the user
 *   changed the paragraph, the drawer showed a change Apply would not write. Here
 *   every view takes hold of the one `LiveProposal` of the card, built on the live
 *   paragraph from the page store, and draws the package Apply writes.
 *
 * HOW
 *   - a card is keyed by its bookmarks and its redline, the two inputs its
 *     proposal is built from; the user's edit is laid over the shared proposal
 *     (`LiveProposal.setEditLayer`), so it is no part of the key;
 *   - a view `acquire`s the card and `release`s it when it goes; the proposal is
 *     started on the first hold, and stopped `IDLE_PROPOSAL_KEPT_FOR_MS` after the
 *     last, unless a view takes hold of it again before then;
 *   - a hold says whether the view shows recovery: a chat card holds a card
 *     "recovering", so a lost paragraph is looked for in Word; the drawer and a
 *     hover hold it "pageOnly", so a card they alone hold reads Word for nothing
 *     and builds from the page the store reads once for every card;
 *   - the last live preview of each card is kept after its last release, so an
 *     applied card keeps, in every view, the look it had when Apply was clicked.
 */
import type { RedlinePayload } from "@common/utils/redline/wire/redline-payload";

import type { AnchorTracker } from "../anchor-tracker";
import type { DocumentPageStore } from "../document-page-store";
import type { WordDocument } from "../word-document";
import { LiveProposal, type LiveProposalState, type ProposalEditLayerInput } from "./live-proposal";

/**
 * CASES
 *   - "recovering" -> the view shows the card's recovery: a lost paragraph is looked for in Word
 *   - "pageOnly"   -> the view reads the page only: a lost paragraph keeps the preview it has
 */
export type LiveProposalHold = "recovering" | "pageOnly";

/** What a card's live proposal is built from. */
export interface LiveProposalRequest {
    bookmarkNames: string[];
    redline: RedlinePayload;
    /** The user's edit of the card, laid over the proposal under the user's name; null for a card nobody edited. */
    editLayer: ProposalEditLayerInput | null;
}

/** One view's hold of a card's live proposal. */
export interface LiveProposalLease {
    readonly proposal: LiveProposal;
    /** Hands the card the view's edit; an edit equal to the one laid changes nothing. */
    setEditLayer(editLayer: ProposalEditLayerInput | null): void;
    /** Ends the hold; the proposal stops when no view holds it any more. Calling it again does nothing. */
    release(): void;
}

/** The preview a card last published while live, with the stamp its own revisions carry and the lines that named its table changes. */
export interface LastLivePreview {
    previewPackageOoxml: string;
    stampedAt: string | null;
    tableChangeLines: string[];
}

/** The one Word document, the trackers and the page store every card is built over. */
export interface LiveProposalRegistryDeps {
    /** The tracker for the bookmarks, with the redline attached; null attaches none. */
    trackerFor(bookmarkNames: string[], redline: RedlinePayload | null): AnchorTracker;
    word: WordDocument;
    pages: DocumentPageStore;
}

/**
 * How long a card's proposal keeps running after its last view lets go.
 *
 * WHY
 *   The chat is a virtualized list: a card scrolled out of view unmounts and lets
 *   go of its proposal, and scrolled back it takes hold again. Stopped at once,
 *   every scroll back built the card again (a new tracker, the engine over the
 *   page, the package), so a chat of table cards scrolled slowly. Kept running for a
 *   while, a card scrolled back finds its
 *   proposal built, as it stands over the page now.
 *
 * EXAMPLE
 *   a card leaves the view at 10:00:00 and comes back at 10:00:20 -> the same proposal, already live
 *   it comes back at 10:01:30                                   -> stopped at 10:01:00, built again
 */
export const IDLE_PROPOSAL_KEPT_FOR_MS = 60_000;

/**
 * A started proposal with the holds on it, each named by the serial its lease was
 * given, and the timer that stops it while no view holds it.
 *
 * NULL
 *   `stopWhenStillIdle` is null while a view holds the proposal.
 */
interface RegistryEntry {
    proposal: LiveProposal;
    holds: Map<number, LiveProposalHold>;
    stopFollowing: () => void;
    stopWhenStillIdle: ReturnType<typeof setTimeout> | null;
}

export class LiveProposalRegistry {
    private readonly entries = new Map<string, RegistryEntry>();
    private readonly lastLivePreviews = new Map<string, LastLivePreview>();
    /** Each card's preview before Word is read, by its key and its edit, built once: null for a card whose redline does not build. */
    private readonly previewsBeforeRead = new Map<string, LastLivePreview | null>();
    /** The cards whose look was frozen when Apply was clicked (`freezeAppliedLook`): no later live publish replaces it. */
    private readonly frozenKeys = new Set<string>();
    private nextHoldSerial = 0;

    constructor(private readonly deps: LiveProposalRegistryDeps) {}

    /**
     * The key a card's proposal is shared under: its bookmarks in order and its redline.
     *
     * EXAMPLE
     *   bookmarks ["_fees"], redline { kind: "texts", … } -> '[["_fees"],{"kind":"texts",…}]'
     */
    static keyOf(request: Pick<LiveProposalRequest, "bookmarkNames" | "redline">): string {
        return JSON.stringify([request.bookmarkNames, request.redline]);
    }

    /**
     * Takes hold of the card's live proposal, starting it when no view held it.
     *
     * CASES
     *   - the card is held already       -> the same proposal; the view's edit is handed to it, and
     *                                       recovery turns on when this hold is "recovering"
     *   - no view holds it, but its proposal
     *     is still kept (`IDLE_PROPOSAL_KEPT_FOR_MS`) -> the same proposal, its stop called off
     *   - neither                        -> a new proposal, started over the page store
     */
    acquire(request: LiveProposalRequest, hold: LiveProposalHold): LiveProposalLease {
        const key = LiveProposalRegistry.keyOf(request);
        const entry = this.entries.get(key) ?? this.started(key, request, hold);
        /** WHEN the proposal was waiting out its idle time CONSEQUENCE it is held again, so it is no longer stopped */
        if (entry.stopWhenStillIdle !== null) {
            clearTimeout(entry.stopWhenStillIdle);
            entry.stopWhenStillIdle = null;
        }
        const serial = this.nextHoldSerial;
        this.nextHoldSerial += 1;
        entry.holds.set(serial, hold);
        entry.proposal.setEditLayer(request.editLayer);
        entry.proposal.setRecoversLostParagraph(LiveProposalRegistry.anyHoldRecovers(entry));
        let released = false;
        return {
            proposal: entry.proposal,
            setEditLayer: (editLayer) => {
                /**
                 * WHEN the lease was released
                 *   CONSEQUENCE
                 *     the edit is dropped: the proposal may already belong to other views, or be stopped.
                 */
                if (released) return;
                entry.proposal.setEditLayer(editLayer);
            },
            release: () => {
                if (released) return;
                released = true;
                this.released(key, entry, serial);
            },
        };
    }

    /** The preview the card last published while live, kept after its last view let go; null when it never went live or was forgotten. */
    lastLivePreview(request: Pick<LiveProposalRequest, "bookmarkNames" | "redline">): LastLivePreview | null {
        return this.lastLivePreviews.get(LiveProposalRegistry.keyOf(request)) ?? null;
    }

    /**
     * The look an applied card keeps: the preview it showed when Apply was clicked.
     * An applied card changes only by gaining its Applied mark; it never turns into
     * the document's own tracked changes, or into another drawing of the card.
     *
     * CASES
     *   - the card went live this session        -> its last live preview
     *   - it never did (applied while off screen,
     *     or the pane was reloaded since)         -> its preview before Word is read, built
     *                                               once from the card (`LiveProposal.previewBeforeRead`)
     *   - its redline does not build             -> null
     */
    appliedLookOf(request: LiveProposalRequest): LastLivePreview | null {
        const kept = this.lastLivePreview(request);
        /** WHEN the card showed a live preview this session CONSEQUENCE that preview, as it was last shown */
        if (kept !== null) return kept;
        const lookKey = JSON.stringify([request.bookmarkNames, request.redline, request.editLayer]);
        /** WHEN the card's preview before Word is read was built already CONSEQUENCE that one, so every view draws one look */
        if (this.previewsBeforeRead.has(lookKey)) return this.previewsBeforeRead.get(lookKey) ?? null;
        const previewBeforeRead = LiveProposal.previewBeforeRead(request);
        this.previewsBeforeRead.set(lookKey, previewBeforeRead);
        return previewBeforeRead;
    }

    /**
     * Freezes the look the card keeps once applied, at the moment Apply is clicked:
     * the preview it shows now, kept as its applied look however the document
     * changes afterwards. Apply, Apply All and a conflict's resolution all call it
     * for each card they write, before the write.
     *
     * WHY
     *   An applied card keeps the look it had when Apply was clicked (#2218, text
     *   cards). A table card's live preview is composed over the whole live table, and
     *   a rebuild over the table after the write still counts the card's row change, so
     *   its newest publish replaced the kept preview with the table as it later stood,
     *   other cards' changes and all (page finding #13, 2026-10-16). Frozen here, every
     *   card kind keeps the picture of its own change over the table as it stood then.
     *
     * CASES
     *   - the card is live now and shows a change -> its preview as shown now
     *   - it shows no change, or is not live now   -> its applied look so far: the last live preview
     *                                                 it published, or its preview before Word is read
     *   - its redline does not build               -> nothing to keep; later publishes are still ignored
     *
     * A second click on the same card (a retry after a failed write) freezes the look
     * shown at that click.
     */
    freezeAppliedLook(request: LiveProposalRequest): void {
        const key = LiveProposalRegistry.keyOf(request);
        const liveState = this.entries.get(key)?.proposal.currentState ?? null;
        const showsAChangeNow = liveState !== null && liveState.previewPackageOoxml !== null && liveState.changeCount > 0 && (liveState.status === "live" || liveState.status === "proposed");
        const lookNow: LastLivePreview | null = showsAChangeNow && liveState.previewPackageOoxml !== null
            ? { previewPackageOoxml: liveState.previewPackageOoxml, stampedAt: liveState.stampedAt, tableChangeLines: liveState.tableChangeLines }
            : this.appliedLookOf(request);
        if (lookNow !== null) this.lastLivePreviews.set(key, lookNow);
        this.frozenKeys.add(key);
    }

    /** Whether the card's look was frozen by a click on Apply (`freezeAppliedLook`). */
    isLookFrozen(request: Pick<LiveProposalRequest, "bookmarkNames" | "redline">): boolean {
        return this.frozenKeys.has(LiveProposalRegistry.keyOf(request));
    }

    /**
     * Forgets every card's last live preview, every frozen look, and every preview
     * built before Word was read. The chat page calls it when its conversation is
     * closed or switched, so the previews kept are those of one conversation's cards
     * and no more.
     *
     * WHY then
     *   The previews are the looks applied cards keep in the conversation that shows
     *   them. A card of a conversation no longer shown needs none, and one shown again
     *   later draws its preview before Word is read, as after a reload.
     */
    forgetLastLivePreviews(): void {
        this.lastLivePreviews.clear();
        this.previewsBeforeRead.clear();
        this.frozenKeys.clear();
        this.stopIdleProposals();
    }

    /** How many cards' last live previews are kept. */
    get rememberedPreviewCount(): number {
        return this.lastLivePreviews.size;
    }

    /** How many cards are held right now; a proposal kept running while no view holds it is not counted. */
    get heldCardCount(): number {
        return Array.from(this.entries.values()).filter((entry) => entry.holds.size > 0).length;
    }

    /** How many proposals are running while no view holds them, waiting out `IDLE_PROPOSAL_KEPT_FOR_MS`. */
    get idleProposalCount(): number {
        return Array.from(this.entries.values()).filter((entry) => entry.holds.size === 0).length;
    }

    /**
     * Stops every proposal no view holds, now, instead of after its idle time.
     *
     * WHY on forgetting
     *   The conversation's cards are gone with it; their proposals would only follow
     *   the page for nobody until their time ran out.
     */
    private stopIdleProposals(): void {
        for (const [key, entry] of Array.from(this.entries)) {
            /** WHEN a view still holds the proposal CONSEQUENCE it keeps running */
            if (entry.holds.size > 0) continue;
            this.stopped(key, entry);
        }
    }

    private started(key: string, request: LiveProposalRequest, hold: LiveProposalHold): RegistryEntry {
        const proposal = new LiveProposal({
            bookmarkNames: request.bookmarkNames,
            redline: request.redline,
            editLayer: request.editLayer,
            tracker: this.trackerOf(request),
            word: this.deps.word,
            pages: this.deps.pages,
            recoversLostParagraph: hold === "recovering",
        });
        const stopFollowing = proposal.subscribe((state) => this.rememberLivePreview(key, state));
        const entry: RegistryEntry = { proposal, holds: new Map(), stopFollowing, stopWhenStillIdle: null };
        this.entries.set(key, entry);
        proposal.start();
        return entry;
    }

    /**
     * The card's tracker, with its redline attached.
     *
     * WHEN attaching the redline throws (a markup card whose markup does not parse)
     *   CONSEQUENCE
     *     the card gets its tracker without it. Its proposal then fails to read the same
     *     redline and shows the error on its own row, while every other card is built:
     *     one card never stops the drawer.
     */
    private trackerOf(request: LiveProposalRequest): AnchorTracker {
        try {
            return this.deps.trackerFor(request.bookmarkNames, request.redline);
        } catch {
            return this.deps.trackerFor(request.bookmarkNames, null);
        }
    }

    /**
     * CASES
     *   - other views still hold the card -> it keeps running; recovery stays on only while one of them recovers
     *   - this was the last hold          -> it keeps running over the page with recovery off, and stops
     *                                        `IDLE_PROPOSAL_KEPT_FOR_MS` later unless a view holds it again
     *
     * WHY recovery off while idle
     *   Recovery looks for a lost paragraph in Word, for a view that shows it. No view
     *   shows an idle card, so it builds from the page store alone, as a drawer row does.
     */
    private released(key: string, entry: RegistryEntry, serial: number): void {
        entry.holds.delete(serial);
        entry.proposal.setRecoversLostParagraph(LiveProposalRegistry.anyHoldRecovers(entry));
        /** WHEN another view still holds the card CONSEQUENCE nothing more: it is in use */
        if (entry.holds.size > 0) return;
        entry.stopWhenStillIdle = setTimeout(() => this.stopped(key, entry), IDLE_PROPOSAL_KEPT_FOR_MS);
    }

    /** Stops the proposal and takes it out of the registry; a view that holds the card later builds it again. */
    private stopped(key: string, entry: RegistryEntry): void {
        /** WHEN its stop was still waiting CONSEQUENCE that wait is called off: this is the stop */
        if (entry.stopWhenStillIdle !== null) clearTimeout(entry.stopWhenStillIdle);
        entry.stopWhenStillIdle = null;
        entry.stopFollowing();
        entry.proposal.stop();
        /** WHEN a newer proposal took the key since CONSEQUENCE it stays: only this entry leaves */
        if (this.entries.get(key) === entry) this.entries.delete(key);
    }

    /**
     * WHEN the card publishes a live preview that changes something
     *   CONSEQUENCE
     *     it is kept as the card's last live preview: what Apply wrote, once the card is applied.
     *
     * WHY not a preview that changes nothing
     *   Apply's write is read back by the page store, and the card may rebuild over its own
     *   applied text before its views learn it was applied: that build changes nothing, and
     *   kept, it would document an applied card as a card that changed nothing.
     *
     * WHY not a publish after Apply was clicked
     *   The card's look was frozen at the click (`freezeAppliedLook`); a rebuild over the
     *   document as it later stands is the live state, never the applied card's picture.
     */
    private rememberLivePreview(key: string, state: LiveProposalState): void {
        if (this.frozenKeys.has(key)) return;
        if (state.status !== "live" || state.previewPackageOoxml === null || state.changeCount === 0) return;
        this.lastLivePreviews.set(key, { previewPackageOoxml: state.previewPackageOoxml, stampedAt: state.stampedAt, tableChangeLines: state.tableChangeLines });
    }

    private static anyHoldRecovers(entry: RegistryEntry): boolean {
        return Array.from(entry.holds.values()).some((hold) => hold === "recovering");
    }
}
